import React from 'react';
import clsx from 'clsx';

import { useArtifactVersionNumber } from '~/components/workspaces/hooks/use-artifact-version-number';
import * as Headless from '@headlessui/react';
import { ChevronUpDownIcon, PencilSquareIcon, SwatchIcon } from '@heroicons/react/24/outline';
import type { CaraWorkspaceFieldsFragment } from '~/api/graphql';
import { CaraWorkspaceArtifactType } from '~/api/graphql';
import { Badge } from '@oysterjs/ui/badge';

import {
  defaultDocumentVersion,
  latestVersion as latestOf
} from '~/components/documents/document-versions';
import { getArtifactTitle } from '~/components/workspaces/lib/artifact';
import {
  changedOnlyDesign,
  writtenByDesignChange
} from '~/components/workspaces/lib/artifact-versions';

type Artifact = CaraWorkspaceFieldsFragment['artifacts'][0];

/** How a version was changed by hand, if it was. */
const VersionChangeBadge = (props: { artifact: Artifact; versions: readonly Artifact[] }) => {
  if (!props.artifact.editedAt) return null;
  return writtenByDesignChange(props.artifact) ||
    changedOnlyDesign(props.artifact, props.versions) ? (
    <Badge color="neutral">
      <SwatchIcon className="size-3" />
      Design changed
    </Badge>
  ) : (
    <Badge color="neutral">
      <PencilSquareIcon className="size-3" />
      Edited
    </Badge>
  );
};

function getArtifactTypeLabel(type: CaraWorkspaceArtifactType): string {
  switch (type) {
    case CaraWorkspaceArtifactType.DocumentHtml:
      return 'Doc';
    case CaraWorkspaceArtifactType.PdfFile:
      return 'PDF';
    case CaraWorkspaceArtifactType.TemplateForm:
      return 'Form';
    case CaraWorkspaceArtifactType.XlsxFile:
      return 'XLSX';
    default:
      return 'File';
  }
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/** Something to do to the version shown, offered in the menu under the versions. */
export type VersionAction = {
  label: string;
  title?: string;
  disabled?: boolean;
  onSelect: () => void;
};

export type ArtifactVersionPickerProps = {
  /** All versions of the same artifact (same id), sorted newest-first by version */
  versions: Artifact[];
  /** All artifacts in the workspace (all ids, all versions) for cross-artifact navigation */
  allArtifacts: Artifact[];
  /** The artifact version currently being viewed */
  currentArtifact: Artifact;
  /** Called when the user selects a different version or a different artifact */
  onVersionChange: (artifact: Artifact) => void;
  /**
   * Who wrote a version: its badge, in place of "Edited", and the row's
   * tooltip. Without it a version a user edited reads "Edited".
   */
  describeVersion?: (artifact: Artifact) => { badge: React.ReactNode; tooltip: string };
  versionAction?: VersionAction;
  disabled?: boolean;
};

export const ArtifactVersionPicker: React.FC<ArtifactVersionPickerProps> = ({
  versions,
  allArtifacts,
  currentArtifact,
  onVersionChange,
  describeVersion,
  versionAction,
  disabled
}) => {
  const artifactVersionNumber = useArtifactVersionNumber();
  const sortedVersions = React.useMemo(
    () => [...versions].sort((a, b) => b.version - a.version),
    [versions]
  );
  const latestVersion = sortedVersions[0];
  const outOfDate = !!latestVersion && currentArtifact.version < latestVersion.version;

  // Other documents: one row per artifact id, opening the version its card or row would, ordered
  // by when each was last written.
  const otherArtifacts = React.useMemo(() => {
    const byId = new Map<string, Artifact[]>();
    for (const art of allArtifacts) {
      if (art.id === currentArtifact.id) continue;
      byId.set(art.id, [...(byId.get(art.id) ?? []), art]);
    }
    const writtenAt = (each: Artifact[]) => new Date(latestOf(each)!.createdAt).getTime();
    return [...byId.values()]
      .sort((a, b) => writtenAt(b) - writtenAt(a))
      .map((each) => defaultDocumentVersion(each)!);
  }, [allArtifacts, currentArtifact.id]);

  return (
    <Headless.Popover className="relative">
      <>
        <Headless.PopoverButton
          disabled={disabled}
          className={clsx(
            'flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors focus:outline-none disabled:opacity-50',
            outOfDate
              ? 'border-amber-400 text-amber-700 hover:border-amber-500 dark:border-amber-500/60 dark:text-amber-400 dark:hover:border-amber-400'
              : 'border-neutral-950/10 hover:border-neutral-950/20 dark:border-white/10 dark:hover:border-white/20'
          )}
          title="Switch version or document"
        >
          <span>{`v${artifactVersionNumber(currentArtifact)}`}</span>
          <span className="text-neutral-400 dark:text-neutral-500">·</span>
          <span>
            {sortedVersions.length} version{sortedVersions.length !== 1 ? 's' : ''}
          </span>
          <ChevronUpDownIcon className="size-3.5 shrink-0" />
        </Headless.PopoverButton>

        <Headless.PopoverPanel
          transition
          anchor="bottom start"
          className="z-50 w-80 mt-1 origin-top-left rounded-lg bg-white dark:bg-neutral-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
        >
          {({ close }) => (
            <div className="max-h-[22rem] overflow-y-auto">
              <div className="py-1">
                {sortedVersions.map((art) => {
                  const isCurrent =
                    art.id === currentArtifact.id && art.version === currentArtifact.version;
                  const isLatest = latestVersion && art.version === latestVersion.version;
                  const described = describeVersion?.(art);

                  return (
                    <button
                      key={`${art.id}-${art.version}`}
                      type="button"
                      title={described?.tooltip}
                      onClick={() => {
                        onVersionChange(art);
                        close();
                      }}
                      className={clsx(
                        'w-full text-left flex flex-col gap-1 px-3 py-2 text-xs transition-colors',
                        !isCurrent &&
                          'hover:bg-neutral-50 dark:hover:bg-white/5 text-neutral-700 dark:text-neutral-300'
                      )}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span
                          className={clsx(
                            'font-medium',
                            !isCurrent && 'text-neutral-500 dark:text-neutral-400'
                          )}
                        >
                          Version {artifactVersionNumber(art)}
                        </span>
                        <span className="shrink-0 text-neutral-400 dark:text-neutral-500">
                          {formatRelativeTime(new Date(art.createdAt))}
                        </span>
                      </span>
                      {/* A line of their own: the latest version can carry three, more than fit
                          beside its name in the popover. */}
                      <span className="flex flex-wrap gap-1 empty:hidden">
                        {isLatest && (
                          <Badge color="sky" className="text-xs">
                            Latest
                          </Badge>
                        )}
                        {described ? (
                          described.badge
                        ) : (
                          <VersionChangeBadge artifact={art} versions={sortedVersions} />
                        )}
                        {art.document?.final && (
                          <Badge color="green" className="text-xs">
                            Final
                          </Badge>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {versionAction && (
                <div className="py-1 border-t border-neutral-100 dark:border-neutral-700">
                  <button
                    type="button"
                    title={versionAction.title}
                    disabled={versionAction.disabled}
                    onClick={() => {
                      close();
                      versionAction.onSelect();
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-medium text-neutral-700 enabled:hover:bg-neutral-50 disabled:opacity-50 dark:text-neutral-300 dark:enabled:hover:bg-white/5"
                  >
                    {versionAction.label}
                  </button>
                </div>
              )}

              {/* Other documents in workspace */}
              {otherArtifacts.length > 0 && (
                <div className="py-1 border-t border-neutral-100 dark:border-neutral-700">
                  {otherArtifacts.map((art) => {
                    const title = getArtifactTitle(art);
                    const typeLabel = getArtifactTypeLabel(art.type);

                    return (
                      <button
                        key={art.id}
                        type="button"
                        onClick={() => {
                          onVersionChange(art);
                          close();
                        }}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors text-neutral-700 dark:text-neutral-300"
                      >
                        <span className="flex-1 font-medium text-neutral-800 dark:text-neutral-200 truncate">
                          {title}
                        </span>
                        <Badge color="neutral" className="text-xs shrink-0">
                          {typeLabel}
                        </Badge>
                        <span className="shrink-0 text-neutral-400 dark:text-neutral-500">
                          {`v${artifactVersionNumber(art)}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Headless.PopoverPanel>
      </>
    </Headless.Popover>
  );
};
